import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Ban, Paperclip, Pencil, Plus, ShieldCheck, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { useAttachComplianceMutation, useComplianceQuery, useOpenDocument, useUpsertComplianceMutation } from '@/client/api/business/spend';
import { COMPLIANCE_KIND_LABEL, COMPLIANCE_STATUS_BADGE, type ComplianceItem, type ComplianceKind } from '@/client/types/business/spend';
import { BusinessPageShell } from '../components/BusinessPageShell';
import { Field, filterClass, inputClass, textareaClass } from '../../Accounting/components/spend-ui';
import { DeleteConfirmDialog, EmptyState, RowMenu, StatusBadge } from '../../HumanResource/components/hr-ui';
import { formatDay } from '../../HumanResource/components/leave-format';
import { useEmployees } from '../../HumanResource/hr-store';

type Form = Partial<ComplianceItem>;

/** Everything the company must hold and keep current, with renewal warnings. */
export default function CompliancePage() {
  const q = useComplianceQuery();
  const upsert = useUpsertComplianceMutation();
  const attach = useAttachComplianceMutation();
  const openDoc = useOpenDocument('business');
  const employees = useEmployees();
  const canManage = useGate(PERMISSIONS.accounting.manage).allowed;
  const fileRef = useRef<HTMLInputElement>(null);
  const [attachFor, setAttachFor] = useState<ComplianceItem | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [revoking, setRevoking] = useState<ComplianceItem | null>(null);
  const [kind, setKind] = useState<ComplianceKind | 'all'>('all');

  const items = q.data ?? [];
  const shown = items.filter((i) => kind === 'all' || i.kind === kind);
  const owner = (id?: string | null) => employees.find((e) => e.id === id)?.name;

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    upsert.mutate(
      { ...form, issuedOn: form.issuedOn || null, expiresOn: form.expiresOn || null, ownerId: form.ownerId || null },
      {
        onSuccess: () => {
          toast.success('Saved');
          setForm(null);
        },
        onError: (err) => toast.error((err as Error).message || 'Could not save'),
      },
    );
  };

  return (
    <BusinessPageShell
      mock={false}
      title="Compliance"
      description="Registrations, licences, permits, certificates, insurance and tax clearance. Each is flagged once it is inside its renewal window."
      crumbs={[{ label: 'Compliance' }]}
      icon={ShieldCheck}
      actions={
        <PermissionButton code={PERMISSIONS.accounting.manage} className="gap-2" onClick={() => setForm({ kind: 'license', name: '', renewLeadDays: 60 })}>
          <Plus className="h-4 w-4" /> Add item
        </PermissionButton>
      }
      stats={[
        { label: 'Valid', value: String(items.filter((i) => i.status === 'valid').length) },
        { label: 'Renew soon', value: String(items.filter((i) => i.status === 'expiring_soon').length) },
        { label: 'Expired', value: String(items.filter((i) => i.status === 'expired').length) },
        { label: 'No document on file', value: String(items.filter((i) => !i.hasDocument && i.status !== 'revoked').length) },
      ]}
    >
      <div className="mb-4">
        <select value={kind} onChange={(e) => setKind(e.target.value as ComplianceKind | 'all')} className={filterClass}>
          <option value="all">Everything</option>
          {Object.entries(COMPLIANCE_KIND_LABEL).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      </div>
      {q.isError ? (
        <EmptyState label={`Could not load the register: ${(q.error as Error)?.message ?? 'unknown error'}`} />
      ) : q.isLoading ? (
        <EmptyState label="Loading the register…" />
      ) : shown.length === 0 ? (
        <EmptyState label="Nothing on the register yet. Start with the business registration." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Item</th>
                <th className="px-4 py-3 font-medium">Issuer</th>
                <th className="px-4 py-3 font-medium">Expires</th>
                <th className="px-4 py-3 font-medium">Owner</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {shown.map((i) => (
                <tr key={i.id} className={canManage ? 'cursor-pointer hover:bg-gray-50' : ''} onClick={() => canManage && setForm(i)}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{i.name}</p>
                    <p className="text-xs text-gray-400">
                      {COMPLIANCE_KIND_LABEL[i.kind]}
                      {i.number && ` · ${i.number}`}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{i.issuer || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {i.expiresOn ? formatDay(i.expiresOn) : 'does not expire'}
                    {i.daysToExpiry != null && i.status !== 'valid' && (
                      <p className="text-xs">{i.daysToExpiry < 0 ? `${-i.daysToExpiry} days ago` : `in ${i.daysToExpiry} days`}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{owner(i.ownerId) ?? '—'}</td>
                  <td className="px-4 py-3">
                    <StatusBadge label={COMPLIANCE_STATUS_BADGE[i.status].label} className={COMPLIANCE_STATUS_BADGE[i.status].className} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <RowMenu
                      items={[
                        { label: 'Edit / renew', icon: Pencil, hidden: !canManage, onSelect: () => setForm(i) },
                        { label: 'Open document', icon: Paperclip, hidden: !i.hasDocument, onSelect: () => openDoc(`/compliance/${i.id}/document`).catch((e) => toast.error((e as Error).message)) },
                        {
                          label: i.hasDocument ? 'Replace document' : 'Attach document',
                          icon: Upload,
                          hidden: !canManage,
                          onSelect: () => {
                            setAttachFor(i);
                            fileRef.current?.click();
                          },
                        },
                        { label: 'Mark revoked', icon: Ban, danger: true, hidden: !canManage || i.status === 'revoked', onSelect: () => setRevoking(i) },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file && attachFor) attach.mutate({ id: attachFor.id, file }, { onSuccess: () => toast.success('Attached'), onError: (err) => toast.error((err as Error).message) });
        }}
      />

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{form?.id ? `Edit ${form.name}` : 'Add to the register'}</DialogTitle>
          </DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Name">
                  <input required value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} placeholder="Registrar-General certificate" />
                </Field>
                <Field label="Kind">
                  <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as ComplianceKind })} className={inputClass}>
                    {Object.entries(COMPLIANCE_KIND_LABEL).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Issued by">
                  <input value={form.issuer ?? ''} onChange={(e) => setForm({ ...form, issuer: e.target.value })} className={inputClass} placeholder="ORC, GRA, NCA…" />
                </Field>
                <Field label="Number">
                  <input value={form.number ?? ''} onChange={(e) => setForm({ ...form, number: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Issued">
                  <input type="date" value={form.issuedOn ?? ''} onChange={(e) => setForm({ ...form, issuedOn: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Expires (empty = never)">
                  <input type="date" value={form.expiresOn ?? ''} onChange={(e) => setForm({ ...form, expiresOn: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Warn this many days before">
                  <input type="number" min={0} max={365} value={form.renewLeadDays ?? 60} onChange={(e) => setForm({ ...form, renewLeadDays: Number(e.target.value) })} className={inputClass} />
                </Field>
                <Field label="Who renews it">
                  <select value={form.ownerId ?? ''} onChange={(e) => setForm({ ...form, ownerId: e.target.value })} className={inputClass}>
                    <option value="">Nobody yet</option>
                    {employees.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label="Notes">
                <textarea rows={2} value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={textareaClass} />
              </Field>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => setForm(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={upsert.isPending}>
                  Save
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!revoking}
        onOpenChange={(o) => !o && setRevoking(null)}
        title={`Mark ${revoking?.name} revoked?`}
        description="It stays on the register as revoked and stops raising renewal warnings."
        actionLabel="Mark revoked"
        onConfirm={() => {
          if (revoking) upsert.mutate({ id: revoking.id, revoked: true }, { onSuccess: () => toast.success('Saved'), onError: (e) => toast.error((e as Error).message) });
          setRevoking(null);
        }}
      />
    </BusinessPageShell>
  );
}
